import * as React from "react";
import { useRef, useEffect, useState } from "react";
import { Box, Typography, Grid, Divider, Button } from "@mui/material";
import { useInView } from "react-intersection-observer";
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from "react-router-dom";

import {
  Text
} from "@chakra-ui/react";

// Custom components
import Card from "./card";
import ContactForm from "./contactForm";

// Images and other files
import VillaImg from "../../resources/images/internal/vorne_links_neu.png";
import BackgroundImg from "../../resources/images/generated/background-white-v1.png";
import CustomizeImg from "../../resources/images/external/BG4-4.jpg";
import QualityImg from "../../resources/images/external/interior-design-quality.jpg";
import ServiceImg from "../../resources/images/external/geschaeftsleute-haende-auf-weissem-tisch-mit-dokumenten-und-entwuerfen.jpg";
import ThumbsUpImage from "../../resources/images/external/nahaufnahme-maenner-die-daumen-hochhalten.jpg";

import exklusivHausprojektImg from "../../resources/images/external/partners/ExklusixHausprojektLogo-New.jpg";
import gigaArchitekciImg from "../../resources/images/external/partners/giga-architekci.png";
import diekmeierInvestmentImg from "../../resources/images/external/partners/Diekmeier-Investment.PNG";
import golankoImmobilienImg from "../../resources/images/external/partners/golanko-immobilien.PNG";

import KevinImg from "../../resources/images/external/partners/golanko-kevin.jpg";
import OliverImg from "../../resources/images/external/partners/diekmeier-oliver.png";

/**
 * Haupt‑Startseite der Anwendung. Der Code wurde vollständig
 * hinsichtlich Barrierefreiheit überarbeitet, ohne Design und Inhalte
 * zu verändern. Wichtige Änderungen:
 *   • Skip‑Link zu Hauptinhalt
 *   • Semantische Überschriften (Typography component‑Prop)
 *   • aria‑Attribute, Rollen und Fokus‑Management
 *   • Alternativtexte für alle Bilder (leere alt‑Attribute für dekorative)
 *   • Tastaturbedienbarkeit für interaktive Bilder/Buttons
 *   • Reduzierte Bewegung, falls vom Nutzer gewünscht (prefers‑reduced‑motion)
 */

const Home = (props) => {
  /* -----------------------------------------------------------
   * State & Refs
   * ---------------------------------------------------------*/
  const prefersReducedMotion = useReducedMotion();
  const windowSize = useRef([window.innerWidth, window.innerHeight]);
  const [scrollHeight, setScrollHeight] = useState("1080px");
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [shownCardsCount, setShownCardsCount] = useState(3);
  const [showNotification, setShowNotification] = useState(false);
  const [targetProjectIndex, setTargetProjectIndex] = useState(null);
  const [cardLG, setCardLG] = useState([]);
  const notificationRef = useRef(null);
  const [ref, inView] = useInView({
    triggerOnce: true, // Animation nur einmal ausführen
    threshold: 0.1,
  });
  const body = document.body;
  const html = document.documentElement;

  /* -----------------------------------------------------------
   * Effekte
   * ---------------------------------------------------------*/
  // Spaltenbreite initialisieren
  useEffect(() => {
    const tempArr = props.data.projects.map(() => 6);
    setCardLG(tempArr);
  }, [props.data.projects]);

  // Responsive‑Breakpoints aktualisieren
  useEffect(() => {
    const onResize = () => setIsSmallScreen(window.innerWidth < 1200);
    onResize();
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Hintergrundbild‑Höhe nachladen
  useEffect(() => {
    setScrollHeight(Math.max(body.offsetHeight, html.offsetHeight));
  }, [body.offsetHeight, html.offsetHeight, cardLG]);

  // Verfügbare Projekte prüfen und Benachrichtigung setzen
  useEffect(() => {
    const availableProjectIndex = props.data.projects.findIndex(
      (project) => project.status.toLowerCase() === "im bau"
    );
    if (availableProjectIndex !== -1) {
      setShowNotification(true);
      setTargetProjectIndex(availableProjectIndex);
    }
  }, [props.data.projects]);

  // Fokus auf Benachrichtigung setzen, sobald sichtbar
  useEffect(() => {
    if (showNotification && notificationRef.current) {
      notificationRef.current.focus();
    }
  }, [showNotification]);

  /* -----------------------------------------------------------
   * Hilfsfunktionen
   * ---------------------------------------------------------*/
  const scrollToProject = (projectIndex) => {
    const targetElement = document.getElementById(`project-${projectIndex}`);
    if (!targetElement) return;
    const navbarHeight = document.querySelector('nav')?.offsetHeight || 0;
    const additionalOffset = 80; // extra Abstand
    const scrollToPosition =
      targetElement.getBoundingClientRect().top + window.scrollY - navbarHeight - additionalOffset;

    window.scrollTo({
      top: scrollToPosition,
      behavior: 'smooth',
    });
  };

  const handleInteractiveKeyDown = (e, callback) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  /* -----------------------------------------------------------
   * Render
   * ---------------------------------------------------------*/
  return (
    <>
      {/* Skip‑Link – wird nur eingeblendet, wenn fokussiert */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          top: -40,
          left: 0,
          background: '#000',
          color: '#fff',
          padding: '8px 16px',
          zIndex: 2000,
          textDecoration: 'none',
        }}
        className="skip-link"
      >
        Zum Inhalt springen
      </a>

      <Grid
        component="main"
        id="main-content"
        container
        sx={{ mt: 0, pt: '80px', display: 'flex' }}
      >
        {/* ---------------------------------------------
         * Projekt‑Benachrichtigung
         * -------------------------------------------*/}
        {showNotification && (
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: -50 }}
            animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            role="region"
            aria-label="Neues Bauprojekt verfügbar"
            aria-live="polite"
            tabIndex={-1}
            ref={notificationRef}
            style={{
              position: 'fixed',
              top: '100px',
              right: '10px',
              width: isSmallScreen ? '320px' : '400px',
              background: 'linear-gradient(135deg, #1E90FF, #4682B4)',
              color: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
              zIndex: 1500,
              outline: 'none',
            }}
          >
            {/* Projektbild */}
            <img
              src={props.data.projects[targetProjectIndex].titleImg}
              alt={props.data.projects[targetProjectIndex].title || 'Projektbild'}
              style={{
                width: '100%',
                borderRadius: '8px',
                marginBottom: '15px',
                objectFit: 'cover',
              }}
            />

            {/* Titel */}
            <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Ein wunderschönes EFH in Berlin Heiligensee ist jetzt verfügbar!
            </Typography>

            {/* Beschreibung */}
            <Typography variant="body2" component="p" sx={{ mb: 2 }}>
              Das EFH befindet sich im Bau und ist ab Q3 2025 bereit für Sie. Schauen Sie in unser Immoscout24 Exposé.
            </Typography>

            {/* Schließen‑Button */}
            <Button
              onClick={() => setShowNotification(false)}
              aria-label="Benachrichtigung schließen"
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                minWidth: 'auto',
                backgroundColor: 'transparent',
                color: 'white',
                fontSize: '16px',
                p: '0 5px',
                '&:hover': { backgroundColor: 'transparent' },
              }}
            >
              ✕
            </Button>

            {/* Aktionen */}
            <Button
              component={Link}
              to={`/project/202500`}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                mr: 1,
                backgroundColor: 'black',
                mb: 0.5
              }}
            >
              Mehr zum Objekt
            </Button>
            <Button
              variant="contained"
              href="https://www.immobilienscout24.de/expose/158833440#/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: '#00ffd0',
                color: 'black',
                '&:hover': { bgcolor: '#CCFFF5' },
                textTransform: 'none',
                mb: 0.5
              }}
            >
              <b>Exposé auf ImmoScout24</b>
            </Button>
          </motion.div>
        )}

        {/* ---------------------------------------------
         * Hauptinhalt
         * -------------------------------------------*/}
        <Grid item xs={12} md={12} alignItems="center" alignContent="center">
          <Grid
            container
            spacing={0}
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 0 }}
          >
            {/* Leerraum */}
            <Grid item sx={{ height: isSmallScreen ? '2vh' : '3vh' }} />

            {/* Mission‑Block */}
            <Grid
              item
              component="section"
              aria-labelledby="mission-heading"
              sx={{
                backgroundColor: 'white',
                p: isSmallScreen ? '1.5em' : '3em',
                borderRadius: 3,
                width: isSmallScreen ? '100%' : '80%',
                color: 'black',
              }}
            >
              <Grid container spacing={3}>
                {props.missionRef && (
                  <Grid item id="mission" xs={12}>
                    <Text
                      id="mission-heading"
                      color="black"
                      as="h2"
                      fontSize="26pt"
                      ref={props.missionRef}
                    >
                      Mission
                    </Text>
                    <Divider sx={{ backgroundColor: 'lightgrey', my: '5px', mb: '20px' }} />

                    <Grid container spacing={3}>
                      <Grid item xs={12} lg={6}>
                        <Text color="black" as="h3" fontSize="22pt" mb={2} fontFamily={"Quicksand"}>
                          Unsere Mission: Ein Zuhause, in dem Sie sich verwirklichen können
                        </Text>
                        <Text color="black" as="p" fontSize="15pt">
                          Wir sind stolz darauf, Ihnen eine umfassende Auswahl an hochwertigen Immobilien
                          anzubieten, die den höchsten Ansprüchen an Qualität, Stil und Komfort gerecht werden.
                          Als Bauunternehmen im Luxussegment verfügt unser Team über langjährige Erfahrung und
                          Expertise in der Planung, Entwicklung und Realisierung von Luxusimmobilien für unsere
                          anspruchsvollen Kunden im Umkreis von Berlin.
                        </Text>
                      </Grid>

                      <Grid item xs={12} lg={6}>
                        <motion.img
                          whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                          transition={{ duration: 0.3 }}
                          src={VillaImg}
                          alt="Visualisierung einer modernen Villa"
                          loading="lazy"
                          style={{ width: '100%', cursor: 'pointer' }}
                          role="button"
                          tabIndex={0}
                          aria-label="Zum verfügbaren Projekt scrollen"
                          onClick={() => scrollToProject(targetProjectIndex)}
                          onKeyDown={(e) => handleInteractiveKeyDown(e, () => scrollToProject(targetProjectIndex))}
                        />
                      </Grid>

                      {isSmallScreen && (
                        <Divider
                          sx={{ backgroundColor: 'lightgrey', my: '5px', mb: '20px', width: '100%' }}
                        />
                      )}

                      {/* Qualität */}
                      <Grid item xs={12} lg={4}>
                        <img
                          src={QualityImg}
                          alt="Handwerker überprüft Bauqualität einer Hauswand"
                          style={{ width: '100%' }}
                        />
                      </Grid>
                      <Grid item xs={12} lg={8}>
                        <Text color="black" as="h3" fontSize="22pt" mb={1} fontFamily={"Quicksand"}>
                          Höchster Anspruch an Qualität
                        </Text>
                        <Text color="black" as="p" fontSize="15pt">
                          Unsere Projekte umfassen exklusive Villen, private Residenzen, luxuriöse Apartments und
                          innovative Wohnkonzepte. Wir setzen auf individuelle Architektur, die sich nahtlos in die
                          Umgebung einfügt und dabei höchste Funktionalität und Ästhetik vereint. Dabei legen wir
                          besonderen Wert auf die Auswahl erstklassiger Materialien und modernster Technologie, um
                          höchste Standards in puncto Nachhaltigkeit, Energieeffizienz und Wohngesundheit zu
                          erreichen.
                        </Text>
                      </Grid>

                      {isSmallScreen && (
                        <Divider
                          sx={{ backgroundColor: 'lightgrey', mt: '25px', width: '100%' }}
                        />
                      )}

                      {/* Maßgeschneiderte Projekte */}
                      <Grid item xs={12} lg={8}>
                        <Text color="black" as="h3" fontSize="22pt" mb={1} fontFamily={"Quicksand"}>
                          Maßgeschneiderte Projekte
                        </Text>
                        <Text color="black" as="p" fontSize="15pt">
                          Unsere Gesellschaft setzt auf maßgeschneiderte Projekte anstelle von Massenfertigung. Sie
                          bieten entscheidende Vorteile im Vergleich zu Standard-Häusern, die uns helfen, Ihren Traum
                          nach einem perfekten Eigenheim zu verwirklichen:
                          <br />
                          <br />
                          1) <b>Personalisierung:</b> Das offensichtlichste Merkmal maßgeschneiderter Projekte ist die
                          Möglichkeit, das Haus an Ihre spezifischen Bedürfnisse und Vorlieben anzupassen. Sie können
                          den Grundriss, die Anzahl der Zimmer, die Größe der Zimmer, die Materialien, die Farben und
                          vieles mehr auswählen, um ein Haus zu schaffen, das perfekt zu Ihren Bedürfnissen und Ihrem
                          Lebensstil passt.
                          <br />
                          <br />
                          2) <b>Bessere Qualität:</b> Maßgeschneiderte Häuser werden in der Regel mit hochwertigeren
                          Materialien gebaut und von erfahrenen Handwerkern errichtet. Das bedeutet, dass Sie ein Haus
                          erhalten, das besser gebaut ist und länger hält als ein Standard-Haus.
                          <br />
                          <br />
                          3) <b>Mehr Zufriedenheit:</b> Letztendlich kann ein maßgeschneidertes Haus dazu beitragen,
                          dass Sie sich in Ihrem Zuhause wohler und zufriedener fühlen, da es auf Ihre spezifischen
                          Bedürfnisse zugeschnitten ist.
                        </Text>
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <img
                          src={CustomizeImg}
                          alt="Architektenskizze maßgeschneiderter Grundriss"
                          style={{ width: '100%' }}
                        />
                        <small className="visually-hidden">Bildquelle: Freepik</small>
                      </Grid>

                      {/* Bester Service */}
                      {isSmallScreen && (
                        <Divider sx={{ backgroundColor: 'lightgrey', my: 2, width: '100%' }} />
                      )}
                      <Grid item xs={12} lg={4}>
                        <img src={ServiceImg} alt="Baubesprechung eines Projektteams" style={{ width: '100%' }} />
                      </Grid>
                      <Grid item xs={12} lg={8}>
                        <Text color="black" as="h3" fontSize="22pt" mb={1} fontFamily={"Quicksand"}>
                          Bester Service
                        </Text>
                        <Text color="black" as="p" fontSize="15pt">
                          Unser erfahrenes Team von Architekten, Designern und Bauexperten steht Ihnen von der ersten
                          Beratung bis zur Fertigstellung Ihres Projekts mit umfassendem Know-how und persönlichem
                          Engagement zur Seite. Wir arbeiten eng mit unseren Kunden zusammen, um ihre individuellen
                          Wünsche und Bedürfnisse zu erfüllen und jedes Projekt zu einem einzigartigen Erlebnis zu
                          machen. Wir setzen alles daran, Ihr Traumhaus zu verwirklichen und Ihnen ein Zuhause zu
                          schaffen, in dem Sie sich rundum wohl fühlen.
                        </Text>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* ------------------------------------------------- */}
            {/* Projekte */}
            {/* ------------------------------------------------- */}
            <Divider sx={{ backgroundColor: 'lightgrey', my: '5px', mb: '20px' }} />
            <Grid
              item
              component="section"
              aria-labelledby="projects-heading"
              sx={{
                backgroundColor: 'white',
                p: '3em',
                borderRadius: 3,
                width: isSmallScreen ? '100%' : '80%',
                color: 'black',
              }}
            >
              <Grid container spacing={3}>
                {props.projectsRef && (
                  <>
                    <Grid item id="projects" xs={12}>
                      <Text id="projects-heading" as="h2" fontSize="26pt" color="black" ref={props.projectsRef}>
                        Projekte
                      </Text>
                      <Divider sx={{ backgroundColor: 'lightgrey', my: '5px', mb: '20px' }} />
                    </Grid>
                    {cardLG &&
                      props.data.projects.map((project, idx) => {
                        if (idx <= shownCardsCount) {
                          return (
                            <Grid key={idx} id={`project-${idx}`} item xs={12} lg={cardLG[idx] || 6}>
                              <Card project={project} setCardLG={setCardLG} cardLG={cardLG} idx={idx} />
                            </Grid>
                          );
                        }
                        return null;
                      })}
                    <Grid item id="project-buttons" xs={12}>
                      {props.data.projects.length < shownCardsCount && (
                        <Button variant="outlined" onClick={() => setShownCardsCount(shownCardsCount + 4)}>
                          Weitere Projekte anzeigen
                        </Button>
                      )}
                      {props.data.projects.length > 3 && shownCardsCount > 3 && (
                        <Button variant="outlined" onClick={() => setShownCardsCount(3)}>
                          Weniger anzeigen
                        </Button>
                      )}
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>

            {/* ------------------------------------------------- */}
            {/* Team */}
            {/* ------------------------------------------------- */}
            <Divider sx={{ backgroundColor: 'lightgrey', my: '5px', mb: '20px' }} />
            <Grid
              item
              component="section"
              aria-labelledby="team-heading"
              sx={{
                backgroundColor: 'white',
                p: '3em',
                borderRadius: 3,
                width: isSmallScreen ? '100%' : '80%',
                color: 'black',
              }}
            >
              <Grid container spacing={3}>
                {props.teamRef && (
                  <>
                    <Grid item xs={12}>
                      <Text
                        id="team-heading"
                        as="h2"
                        fontSize="26pt"
                        color="black"
                        ref={props.teamRef}
                      >
                        Team
                      </Text>
                      <Divider
                        sx={{ backgroundColor: 'lightgrey', my: '5px', mb: '20px' }}
                      />
                    </Grid>
                    {/* Team Intro */}
                    <Grid item xs={12}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} lg={7}>
                          <Text as="h3" fontSize="22pt" mb={2} color="black">
                            Unser Team: Ein Verbund für die Exzellenz
                          </Text>
                          <Text as="p" fontSize="15pt" color="black">
                          Wir, das Team der BDG Wohnbau GmbH und unserer Partner, zeichnen uns durch erstklassige Leistungen und außergewöhnliche Qualität aus. Dank der Expertise und dem Engagement jedes einzelnen Mitglieds sind wir in der Lage, Projekte jeder Größe und Komplexität erfolgreich zu realisieren. Wir verfügen über umfassende Erfahrung in allen Bereichen des Bauwesens – von der Planung und Gestaltung bis hin zur Ausführung und Fertigstellung. Unser Ruf als verlässliches Unternehmen gründet auf höchster Professionalität und Effizienz. Wir setzen alles daran, die Erwartungen unserer Kunden nicht nur zu erfüllen, sondern zu übertreffen. Mit unserer Leidenschaft für Qualität und Exzellenz sind wir stolz darauf, jedes Projekt mit größter Sorgfalt und Präzision umzusetzen.
                          </Text>
                        </Grid>
                        <Grid item xs={12} lg={5}>
                          <img
                            src={ThumbsUpImage}
                            alt="Team gibt Daumen hoch"
                            style={{ width: '100%' }}
                          />
                          <small className="visually-hidden">Bildquelle: Freepik</small>
                        </Grid>
                      </Grid>
                    </Grid>
                    {/* Ansprechpartner */}
                    <Grid item xs={12}>
                      <Text as="h3" fontSize="22pt" mb={2} color="black">
                        Ihre Ansprechpartner
                      </Text>
                      <Divider
                        sx={{ backgroundColor: 'lightgrey', my: '5px', mb: '20px' }}
                      />
                      <Grid container spacing={4}>
                        {/* Kevin */}
                        <Grid item xs={12} md={6}>
                          <Grid container spacing={2}>
                            <Grid item xs={5}>
                              <img
                                src={KevinImg}
                                alt="Portrait von Kevin Golanko – Verkaufsleiter"
                                style={{ width: '100%', borderRadius: 8 }}
                              />
                            </Grid>
                            <Grid item xs={7}>
                              <Text as="h4" fontSize="18pt" mb={0}>
                                Kevin Golanko
                              </Text>
                              <Text as="p" fontSize="14pt" mt={0} mb={1}>
                                Verkaufsleiter
                              </Text>
                              <ul style={{ paddingLeft: '1em', marginTop: '0.5em' }}>
                                <li>Generelle Fragen zu verfügbaren Objekten</li>
                                <li>Besichtigungen und Termine vor Ort</li>
                                <li>Kauf- und Mietabwicklungsprozess</li>
                              </ul>
                            </Grid>
                          </Grid>
                        </Grid>
                        {/* Oliver */}
                        <Grid item xs={12} md={6}>
                          <Grid container spacing={2}>
                            <Grid item xs={5}>
                              <img
                                src={OliverImg}
                                alt="Portrait von Oliver Diekmeier – Geschäftsführer"
                                style={{ width: '100%', borderRadius: 8 }}
                              />
                            </Grid>
                            <Grid item xs={7}>
                              <Text as="h4" fontSize="18pt" mb={0}>
                                Oliver Diekmeier
                              </Text>
                              <Text as="p" fontSize="14pt" mt={0} mb={1}>
                                Geschäftsführer
                              </Text>
                              <ul style={{ paddingLeft: '1em', marginTop: '0.5em' }}>
                                <li>Generelle Fragen zu Projekten und der Gesellschaft</li>
                                <li>Fragen zum Miet-/Kaufprozess</li>
                                <li>Fragen zur Einrichtung und Umsetzung</li>
                              </ul>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    {/* Partnernetzwerk */}
                    <Grid item xs={12}>
                      <Text as="h3" fontSize="22pt" mb={2} color="black">
                        Mit unseren Partnern zum Erfolg
                      </Text>
                      <Divider
                        sx={{ backgroundColor: 'lightgrey', my: '5px', mb: '20px' }}
                      />
                      <Text as="p" fontSize="15pt" color="black">
                      Das exzellente Partnernetzwerk ist eine unserer Stärken: Wir haben sorgfältig ein Netzwerk von Partnern aufgebaut, die Experten in verschiedenen Bereichen des Bauwesens sind. Unsere Partner haben sich durch jahrelange Erfahrung und Fachkenntnisse in ihrem jeweiligen Bereich ausgezeichnet. Das Partnernetzwerk der BDG Wohnbau GmbH umfasst Architekten, Ingenieure, Wirtschaftsingenieure, Subunternehmer, Lieferanten und andere wichtige Akteure in der Baubranche. Jeder Partner wird sorgfältig ausgewählt und überprüft, um sicherzustellen, dass er den hohen Standards unserer Gesellschaft entspricht. Durch die Zusammenarbeit mit einem breiten Spektrum an Partnern können wir eine Vielzahl von Leistungen anbieten und eine große Bandbreite an Projekten erfolgreich durchführen. Die Partnerschaften ermöglichen auch eine nahtlose Koordination und Kommunikation zwischen den verschiedenen Akteuren, was zu einer reibungslosen und effizienten Durchführung von Projekten führt. Die größte Stärke unseres Partnernetzwerks liegt in der Fähigkeit, die besten Talente und Experten der Branche zusammenzubringen, um ein gemeinsames Ziel zu erreichen - höchste Qualität und Kundenzufriedenheit.
                      </Text>
                      <br />
                      <Text as="p" fontSize="15pt" color="black">
                        <b>Zu unseren Partnern zählen unter anderem:</b>
                      </Text>
                      <Grid container spacing={3} alignItems="center" sx={{ my: '1em' }}>
                        {/* ImmoScout24 */}
                        <Grid item xs={12} lg={2} textAlign="center">
                          <img
                            src={"https://www.immobilienscout24.de/content/is24/deu/www/de/anbieten/gewerbliche-anbieter/inserieren/weitere-produkte/partner-und-experte-siegel/_jcr_content/par/flexigrid_1169050838/column2/image_876842761.img.png/1709124718358.png"}
                            alt="Logo ImmoScout24"
                            style={{ width: '80%', marginBottom: '-25px' }}
                          />
                          <Text as="p" color="black">
                            <b>Vermarktung</b>
                          </Text>
                        </Grid>
                        {/* Diekmeier Investment */}
                        <Grid item xs={12} lg={2} textAlign="center">
                          <img
                            src={diekmeierInvestmentImg}
                            alt="Logo Diekmeier Investment GmbH"
                            style={{ width: '100%' }}
                          />
                          <Text as="p" color="black">
                            <b>Verwaltung & Finanzierung</b>
                          </Text>
                        </Grid>
                        {/* Giga Architekci */}
                        <Grid item xs={12} lg={3} textAlign="center">
                          <img
                            src={gigaArchitekciImg}
                            alt="Logo Giga Architekci"
                            style={{ width: '100%' }}
                          />
                          <Text as="p" color="black">
                            <b>Architektur</b>
                          </Text>
                        </Grid>
                        {/* Golanko Immobilien */}
                        <Grid item xs={12} lg={2} textAlign="center">
                          <img
                            src={golankoImmobilienImg}
                            alt="Logo Golanko Immobilien GmbH"
                            style={{ width: '100%' }}
                          />
                          <Text as="p" color="black">
                            <b>Vermarktung & Vertrieb</b>
                          </Text>
                        </Grid>
                        {/* Exklusivhaus Projekt */}
                        <Grid item xs={12} lg={2} textAlign="center">
                          <img
                            src={exklusivHausprojektImg}
                            alt="Logo Exklusivhaus Projekt"
                            style={{ width: '100%' }}
                          />
                          <Text as="p" color="black">
                            <b>Bauplanung & Umsetzung</b>
                          </Text>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>

            {/* ------------------------------------------------- */}
            {/* Kontaktformular */}
            {/* ------------------------------------------------- */}
            <Divider sx={{ backgroundColor: 'lightgrey', my: '5px', mb: '20px' }} />
            <Grid
              item
              component="section"
              aria-labelledby="contact-heading"
              sx={{
                backgroundColor: 'white',
                p: '3em',
                borderRadius: 3,
                width: isSmallScreen ? '100%' : '80%',
                color: 'black',
              }}
            >
              <Grid container spacing={3}>
                {props.contactRef && (
                  <>
                    <Grid item xs={12}>
                      <ContactForm isSmallScreen={isSmallScreen} ref={props.contactRef} />
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* ---------------------------------------------
         * Dekoratives Hintergrundbild (aria-hidden)
         * -------------------------------------------*/}
        <Box
          sx={{
            backgroundImage: `url('${BackgroundImg}')`,
            backgroundRepeat: 'repeat-y',
            backgroundSize: '100%',
            opacity: 0.4,
            width: '100%',
            height: scrollHeight,
            position: 'absolute',
            zIndex: -1,
          }}
          aria-hidden="true"
        />
      </Grid>
    </>
  );
};

export default Home;
